import { LAST_BASELINE_STEP } from './baseline/baseline-steps';
import {
  ALL_SIGNUP_STEPS,
  signupStepPath,
  signupStepsFor,
  type SignupPath,
  type SignupStepId,
} from './steps';
import type { WrapUpContext } from './wrap-up/branching';
import { WRAP_UP_STEPS } from './wrap-up/wrap-up-steps';

/** The page a minor's flow ends on in place of the download step. */
const MINOR_END_STEP: SignupStepId = 'registered';

/**
 * What the flow already knows about this member that changes which steps they
 * will see. Anything not known yet is set to the usual route, so the bar
 * firms up as the member answers rather than guessing at rarer branches.
 */
export interface SignupProgressFacts {
  /** The sponsor asks health-plan members for their plan details. */
  planDetails: boolean;
  /** The wrap-up switches, read the same way the wrap-up routes on them. */
  wrapUp: WrapUpContext;
}

/**
 * Steps nobody is routed to ahead of time: the adjust step only follows a
 * "no" on the typical week, and "no times available" only follows a booking
 * with no slots. They count once the member is on them.
 */
const ON_ARRIVAL_STEPS: ReadonlySet<SignupStepId> = new Set([
  LAST_BASELINE_STEP,
  'booking-unavailable',
]);

/**
 * The wrap-up screens this member will see, in the order the wrap-up routes
 * them: the HDHP notice and its booking; a minor's booking; or, for a sponsor
 * with the call-or-chat question, rewards first when on, then the question,
 * then the call. The call-or-chat answer is not known ahead, so the call (the
 * longer route) counts until the member chooses.
 */
function wrapUpStepsFor(context: WrapUpContext): SignupStepId[] {
  if (!context.onboardingCall) return [];
  if (context.hdhpRequired) return ['hdhp-notice', 'hdhp-booking'];
  if (context.minor) return ['booking'];
  const call: SignupStepId[] = [
    ...(context.languagePreference ? (['language-preference'] as const) : []),
    'booking',
  ];
  if (context.callChoice && !context.programsUnavailable) {
    return [
      ...(context.rewards ? (['rewards-intro', 'rewards-points'] as const) : []),
      'call-choice',
      ...call,
    ];
  }
  return call;
}

/**
 * The steps this member will see on their path, in order. The wrap-up router
 * only redirects, so it is not a step of its own; a minor ends on the
 * registered page instead of the download step.
 */
export function signupProgressSteps(path: SignupPath, facts: SignupProgressFacts): SignupStepId[] {
  const wrapUp = new Set<SignupStepId>(WRAP_UP_STEPS);
  const steps: SignupStepId[] = [];
  for (const step of signupStepsFor(path)) {
    if (step === 'plan-details' && !facts.planDetails) continue;
    if (ON_ARRIVAL_STEPS.has(step)) continue;
    if (step === WRAP_UP_STEPS[0]) {
      steps.push(...wrapUpStepsFor(facts.wrapUp));
      continue;
    }
    if (wrapUp.has(step)) continue;
    steps.push(step === 'download' && facts.wrapUp.minor ? MINOR_END_STEP : step);
  }
  return steps;
}

/**
 * How full the header progress bar is on `step`: its place among the steps
 * this member will see, counted from one, so the first step already shows a
 * sliver and the last step is full. A step on the path that was not expected
 * (the adjust step, the router, a branch the facts did not predict) is slotted
 * in at its place in the path's order, so the bar still moves forward on it.
 * Null for any page that is not a step on this path, which shows no bar.
 */
export function signupProgressFor(
  step: SignupStepId,
  path: SignupPath,
  facts: SignupProgressFacts,
): number | null {
  const steps = signupProgressSteps(path, facts);
  const at = steps.indexOf(step);
  if (at >= 0) return (at + 1) / steps.length;
  // The path's own order (a minor's end page last) places an unexpected step.
  const order: readonly SignupStepId[] = [...signupStepsFor(path), MINOR_END_STEP];
  if (!order.includes(step)) return null;
  const place = order.indexOf(step);
  const before = steps.filter((other) => order.indexOf(other) < place).length;
  return (before + 1) / (steps.length + 1);
}

/** The step a browser path shows (with or without its trailing slash), or null for any other page. */
export function signupStepForPathname(pathname: string): SignupStepId | null {
  const withSlash = pathname.endsWith('/') ? pathname : `${pathname}/`;
  return ALL_SIGNUP_STEPS.find((step) => signupStepPath(step) === withSlash) ?? null;
}
